import { isMockServices } from 'src/utils';
import { MutationFunction } from '@tanstack/react-query';
import {
  LoanChecklist,
  LoanChecklistMetadata,
  LoanChecklistRule,
} from '../loans/types';
import mock from '../utils/mock';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface SaveTenantChecklistParams {
  tenantChecklistId: string | null;
  name: string;
  metadata: LoanChecklistMetadata;
  batchCreateRules?: LoanChecklistRule[];
  active: boolean;
  isGlobal: boolean;
  assignedFromLoanId: string | null;
  lastModifiedUserId: string | null;
  displayOrder?: number | null;
}

interface SaveTenantChecklistResponse {
  checklistRule: LoanChecklist;
  success: boolean;
}

const saveTenantChecklist: MutationFunction<
  LoanChecklist,
  SaveTenantChecklistParams
> = async (args) => {
  if (isMockServices()) {
    return await mock(_mockSaveTenantChecklist.checklistRule, {
      delay: 2000,
      failureRate: 0,
    });
  }

  const url =
    args.tenantChecklistId == null
      ? `/checklist-rules`
      : `/checklist-rules/${args.tenantChecklistId}`;
  const method = args.tenantChecklistId == null ? 'POST' : 'PUT';
  const body = {
    name: args.name,
    metadata: args.metadata,
    active: args.active,
    isGlobal: args.isGlobal,
    assignedFromLoanId: args.assignedFromLoanId,
    lastModifiedUserId: args.lastModifiedUserId,
    displayOrder: args.displayOrder,
    ...(args.batchCreateRules
      ? {
          // Send the id for rules that already exist so the API updates them in place
          // instead of re-creating them under a new id. Loan checklist copies store
          // the rule id they were copied from, and checklist sync matches on it.
          batchCreateRules: args.batchCreateRules.map((rule) => ({
            ...(rule.id ? { id: rule.id } : {}),
            name: rule.name,
            description: rule.description,
            definition: rule.definition,
          })),
        }
      : {}),
  };

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<SaveTenantChecklistResponse>(url, {
    method: method,
    body: body,
  });

  if (response.data) {
    return response.data.checklistRule;
  }
  throw new Error('Failed to save tenant checklist');
};

export default saveTenantChecklist;

const _mockSaveTenantChecklist = {
  checklistRule: {
    id: 'mock-checklist-id',
    name: 'Mock checklist',
    metadata: {},
    active: true,
    isGlobal: true,
    rules: [],
    assignedFromLoanId: null,
    lastModifiedUserId: null,
  } as unknown as LoanChecklist,
} as { checklistRule: LoanChecklist };
